import { motion } from 'framer-motion'
import { Star, TrendingUp, Users } from 'lucide-react'

import { ProgressRing } from '@/components/dashboard/ProgressRing'
import { useCommandDashboard } from '@/contexts/CommandDashboardContext'

export function BusinessSnapshot() {
  const { data } = useCommandDashboard()
  const snapshot = data?.snapshot ?? {}
  const guests = snapshot.guests ?? {}
  const performance = snapshot.performance ?? {}
  const retention = Number(snapshot.retention_pct ?? 0)

  const guestTiles = [
    { label: 'New', value: String(guests.new ?? 0), icon: Users },
    { label: 'Returning', value: String(guests.returning ?? 0), icon: TrendingUp },
    { label: 'VIP', value: String(guests.vip ?? 0), icon: Star },
    { label: 'Birthdays', value: String(guests.birthdays ?? 0), icon: Star },
  ]

  const perfTiles = [
    {
      label: 'Top therapist',
      value: performance.top_therapist ?? '—',
      sub: 'By appointments today',
    },
    {
      label: 'Top service',
      value: performance.top_service ?? '—',
      sub: 'On calendar today',
    },
    {
      label: 'Avg ticket',
      value: performance.avg_ticket ? `AED ${performance.avg_ticket}` : '—',
      sub: 'Completed sales today',
    },
    {
      label: 'Repeat guests',
      value: performance.repeat_pct != null ? `${performance.repeat_pct}%` : '—',
      sub: `${guests.returning ?? 0} returning`,
    },
  ]

  return (
    <motion.section
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="card-surface flex h-full flex-col p-4 lg:p-5"
    >
      <h2 className="text-sm font-bold tracking-tight text-foreground">Business snapshot</h2>
      <p className="mt-0.5 text-[11px] text-muted-foreground">Guests & performance at a glance</p>

      <p className="mt-4 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/80">
        Guests today
      </p>
      <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {guestTiles.map((g) => (
          <div
            key={g.label}
            className="rounded-xl bg-gradient-to-br from-muted/30 to-muted/10 px-3 py-2.5 text-center shadow-sm"
          >
            <g.icon className="mx-auto h-3.5 w-3.5 text-primary opacity-70" />
            <p className="mt-1 text-lg font-bold tabular-nums">{g.value}</p>
            <p className="text-[10px] text-muted-foreground">{g.label}</p>
          </div>
        ))}
      </div>

      <p className="mt-4 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/80">
        Performance
      </p>
      <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
        {perfTiles.map((p) => (
          <div
            key={p.label}
            className="rounded-xl bg-white/60 px-3 py-2.5 shadow-sm"
            style={{ border: '1px solid hsl(262 20% 50% / 0.05)' }}
          >
            <p className="text-[10px] text-muted-foreground">{p.label}</p>
            <p className="mt-0.5 text-sm font-bold leading-tight">{p.value}</p>
            <p className="text-[10px] text-emerald-600">{p.sub}</p>
          </div>
        ))}
      </div>

      <div className="mt-4 flex items-center gap-3 rounded-2xl bg-gradient-to-r from-primary/8 to-primary/4 px-3 py-3">
        <ProgressRing value={retention} size={48} stroke={4}>
          <span className="text-[10px] font-bold text-primary">{retention}%</span>
        </ProgressRing>
        <div>
          <p className="text-sm font-semibold text-foreground">Retention {retention}%</p>
          <p className="text-[11px] text-muted-foreground">Repeat guests with prior visits</p>
        </div>
      </div>
    </motion.section>
  )
}
